import { Map } from 'immutable';

import { getRetainFromVariations, updateTemplateForRetain } from '../../variations/retain';
import { getOperationsFromProduct } from '../../product/getOperationsFromProduct';
import { shapeNewProductForAPI } from '../../product/shapeForAPI';
import { shapeProductForApp } from '../../product/shapeForApp';
import { filterOperations } from '../../product/operations';
import { convertProduct } from '../../product/convert';
import { formHasErrors } from '../../product/formHasErrors';
import { getSize } from '../../iterable/getSize';

import { AMOUNT, ERROR, NEW, OPERATIONS, VALUE } from '../../../constants/product';
import { RETAIN, VARIANTS } from '../../../constants/variations';
import { ETSY, SHOPIFY } from '../../../constants/channels';
import { CHANNEL_ID } from '../../../constants/channels';
import { STATUS } from '../../../constants/listings';
import { EMPTY } from '../../../constants';
import { FORM } from '../../../constants/forms';
import { ALL } from '../../../constants/profiles';
import ATTRIBUTE from '../../../constants/attributes';

export function setIsComplete({ sections, template }) {
  if (!template) return undefined;

  function reduceSections(valid, selected, section) {
    switch (section) {
      case ALL: {
        return valid;
      }

      default: {
        return selected
          ? valid && !formHasErrors({ product: template, type: section })
          : valid;
      }
    }
  }

  return template.set('isComplete', sections.reduce(reduceSections, true));
}

export function shapeTemplateForApp({ channel, data, sections, shopId, templateId: productId }) {
  const product = shapeProductForApp({ asProfile: true, channel, data, productId, shopId });
  const variations = product.get(FORM.VARIATIONS);
  const template = product.setIn([FORM.VARIATIONS, RETAIN], getRetainFromVariations({ channel, variations }));
  return setIsComplete({ sections, template });
}

export function getTemplateOperationsForProducts({ channel, profile }) {
  const sections = profile.get('sections');
  const template = updateTemplateForRetain({ channel, template: profile.get('template') });

  function filterDetailsOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].CATEGORY:
          case OPERATIONS[ETSY].DIGITAL:
          case OPERATIONS[ETSY].IS_SUPPLY:
          case OPERATIONS[ETSY].MATERIALS.CHANGE_TO:
          case OPERATIONS[ETSY].PRODUCTION_PARTNERS:
          case OPERATIONS[ETSY].SECTION:
          case OPERATIONS[ETSY].SHOULD_AUTO_RENEW:
          case OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES:
          case OPERATIONS[ETSY].WHEN_MADE:
          case OPERATIONS[ETSY].WHO_MADE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].CATEGORY:
          case OPERATIONS[SHOPIFY].COLLECTIONS:
          case OPERATIONS[SHOPIFY].PRODUCT_TYPE:
          case OPERATIONS[SHOPIFY].VENDOR: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterFilesOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].FILES.ADD:
          case OPERATIONS[ETSY].FILES.CHANGE_TO:
          case OPERATIONS[ETSY].FILES.DELETE:
          case OPERATIONS[ETSY].FILES.REPLACE:
          case OPERATIONS[ETSY].FILES.SWAP: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterInventoryOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].QUANTITY:
          case OPERATIONS[ETSY].SKU: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterPersonalizationOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].PERSONALIZATION.CHAR_COUNT_MAX_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.IS_PERSONALIZABLE_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.IS_REQUIRED_SET: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterSearchEngineOptimisatonOperations(operation) {
    switch (channel) {
      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].META_DESCRIPTION:
          case OPERATIONS[SHOPIFY].PAGE_TITLE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterShippingOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].WEIGHT:
          case OPERATIONS[ETSY].LENGTH:
          case OPERATIONS[ETSY].WIDTH:
          case OPERATIONS[ETSY].HEIGHT:
          case OPERATIONS[ETSY].RETURN_POLICY:
          case OPERATIONS[ETSY].SHIPPING_PROFILE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterOperationsBySection({ section, operations }) {
    switch (section) {
      case FORM.PHOTOS: {
        return operations.filter(filterOperations(/^photos/, false));
      }

      case FORM.VIDEOS: {
        return operations.filter(filterOperations(/^videos/, false));
      }

      case FORM.TITLE: {
        return operations.filter(filterOperations(OPERATIONS[channel].TITLE, false));
      }

      case FORM.DESCRIPTION: {
        return operations.filter(filterOperations(OPERATIONS[channel].DESCRIPTION, false));
      }

      case FORM.TAGS: {
        return operations.filter(filterOperations(/^tags/, false));
      }

      case FORM.DETAILS: {
        return operations.filter(filterOperations(filterDetailsOperations));
      }

      case FORM.PRICING: {
        switch (channel) {
          case ETSY: {
            return operations.filter(filterOperations(OPERATIONS[ETSY].PRICE, false));
          }

          case SHOPIFY: {
            return (
              sections.get(FORM.INVENTORY) ||
              sections.get(FORM.SHIPPING) || (
                sections.get(FORM.VARIATIONS) &&
                getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) > 0
              )
            )
              ? operations
              : operations.filter(filterOperations(OPERATIONS[SHOPIFY].VARIATIONS, false));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.SEO: {
        switch (channel) {
          case SHOPIFY: {
            return operations.filter(filterOperations(filterSearchEngineOptimisatonOperations));
          }
          default: {
            return operations;
          }
        }
      }

      case FORM.INVENTORY: {
        switch (channel) {
          case ETSY: {
            return operations.filter(filterOperations(filterInventoryOperations));
          }

          case SHOPIFY: {
            return (
              sections.get(FORM.PRICING) ||
              sections.get(FORM.SHIPPING) || (
                sections.get(FORM.VARIATIONS) &&
                getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) > 0
              )
            )
              ? operations
              : operations.filter(filterOperations(OPERATIONS[SHOPIFY].VARIATIONS, false));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.VARIATIONS: {
        switch (channel) {
          case ETSY: {
            return operations.filter(filterOperations(OPERATIONS[channel].VARIATIONS, false));
          }

          case SHOPIFY: {
            return (
              sections.get(FORM.INVENTORY) ||
              sections.get(FORM.PRICING) ||
              sections.get(FORM.SHIPPING)
            )
              ? operations
              : operations.filter(filterOperations(OPERATIONS[channel].VARIATIONS, false));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.PERSONALIZATION: {
        switch (channel) {
          case ETSY: {
            return operations.filter(filterPersonalizationOperations);
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.SHIPPING: {
        switch (channel) {
          case ETSY: {
            return sections.get(FORM.DETAILS) && template.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])
              ? operations
              : operations.filter(filterShippingOperations);
          }

          case SHOPIFY: {
            return (
              sections.get(FORM.INVENTORY) ||
              sections.get(FORM.PRICING) || (
                sections.get(FORM.VARIATIONS) &&
                getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) > 0
              )
            )
              ? operations
              : operations.filter(filterOperations(OPERATIONS[SHOPIFY].VARIATIONS, false));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.FILES: {
        switch (channel) {
          case ETSY: {
            return sections.get(FORM.DETAILS) && template.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])
              ? operations
              : operations.filter(filterFilesOperations);
          }

          default: {
            return operations;
          }
        }
      }

      default: {
        return operations;
      }
    }
  }

  function reduceSections(operations, selected, section) {
    switch (section) {
      case ALL: {
        return operations;
      }

      default: {
        return selected
          ? operations
          : filterOperationsBySection({ section, operations });
      }
    }
  }

  return sections.get(ALL)
    ? getOperationsFromProduct({ channel, product: template })
    : sections.reduce(reduceSections, getOperationsFromProduct({ channel, product: template }));
}

export function convertProductToTemplate({ channel, product }) {
  const variations = product.get(FORM.VARIATIONS)
    .set('currentProfile', Map({ profileId: AMOUNT.MINUS_ONE }))
    .set(RETAIN, getRetainFromVariations({ channel, variations: product.get(FORM.VARIATIONS) }));

  const template = updateTemplateForRetain({ channel, template: product.set(FORM.VARIATIONS, variations) });
  const operations = getOperationsFromProduct({ channel, product: template });

  return template.set('productId', NEW).set('operations', operations);
}

export function shapeTemplateForAPI({ channel, db, shopId, template }) {
  let product = template;

  if (product.get('shopId') !== shopId) {
    product = product.set('shopId', shopId);
  }

  if (product.get('channel') !== channel) {
    product = product.set('productId', NEW).set(ERROR, EMPTY.MAP);
    product = convertProduct({ from: product.get('channel'), to: channel, product, validate: true });
    product = updateTemplateForRetain({ channel, template: product });
    product = product.set('operations', getOperationsFromProduct({ channel, product }));
  } else {
    product = updateTemplateForRetain({ channel, template: product });
  }

  return {
    channel_id: CHANNEL_ID[channel],
    operations: shapeNewProductForAPI({ channel, product, shopId, status: STATUS.TEMPLATE }),
    shopId,
    userDb: db,
  };
}
